import { useEffect, useRef, useState } from 'react'
import { Button } from '../../components/ui/button'
import { PexIcon } from '../../components/icons/PexIcon'
import { useT } from '../../i18n'
import { captureReportViewport } from '../../lib/problem-reporting/capture'
import {
  assertReportRequest,
  reportRequestContext,
  settleReportRequest,
} from '../../lib/problem-reporting/context'
import { reportCopy } from './copy'
import type { createReportSubmission, SubmissionState } from './submission'
import ImageEditor from './ImageEditor'
import ReportVideo from './ReportVideo'
import { reportRecordingAvailable } from '../../lib/problem-reporting/recording'
export type Submission = ReturnType<typeof createReportSubmission>
export function ReportImages({
  controller,
  state,
  editable,
  approved,
  onApproval,
  onRecord,
  recordingBusy,
  recordingErrorMessage,
}: {
  controller: Submission
  state: SubmissionState
  editable: boolean
  approved: ReadonlySet<string>
  onApproval: (url: string, checked: boolean) => void
  onRecord: () => void
  recordingBusy: boolean
  recordingErrorMessage: string | null
}) {
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])
  const t = useT(reportCopy)
  const input = useRef<HTMLInputElement>(null)
  const [capturing, setCapturing] = useState(false)
  const [error, setError] = useState(false)
  const capture = useRef<AbortController | null>(null)
  useEffect(
    () => () => {
      capture.current?.abort()
    },
    [],
  )
  async function takeCapture() {
    if (capture.current) return
    const request = new AbortController()
    capture.current = request
    const context = reportRequestContext()
    setCapturing(true)
    setError(false)
    try {
      const blob = await captureReportViewport({ signal: request.signal })
      await settleReportRequest(context, request.signal)
      await controller.addImage(blob)
    } catch {
      if (!request.signal.aborted) setError(true)
    } finally {
      if (!request.signal.aborted) {
        setCapturing(false)
        capture.current = null
      }
    }
  }
  return (
    <section aria-label={t.images} className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium">
          {t.images}{' '}
          <span className="font-normal text-[var(--color-text-muted)]">
            · {t.optional}
          </span>
        </h3>
        <span className="text-xs">{state.images.length} / 3</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <input
          ref={input}
          aria-label={t.choose}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          hidden
          onChange={async (event) => {
            const files = Array.from(event.target.files ?? [])
            event.target.value = ''
            const context = reportRequestContext()
            for (const file of files.slice(0, 3 - state.images.length)) {
              if (!alive.current) break
              try {
                assertReportRequest(context)
              } catch {
                break
              }
              await controller.addImage(file)
            }
          }}
        />
        <Button
          variant="outline"
          size="sm"
          disabled={
            !editable ||
            state.preparing ||
            capturing ||
            state.images.length >= 3
          }
          onClick={() => input.current?.click()}
        >
          <PexIcon name="image" size="xs" className="mr-2" />
          {t.choose}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={
            !editable ||
            state.preparing ||
            capturing ||
            state.images.length >= 3
          }
          onClick={() => void takeCapture()}
        >
          <PexIcon name="preview" size="xs" className="mr-2" />
          {capturing ? t.loading : t.capture}
        </Button>
        <Button variant="outline" size="sm"
          disabled={!editable || state.preparing || capturing || recordingBusy || state.images.length >= 3 || !reportRecordingAvailable()}
          onClick={onRecord}>{t.record}</Button>
      </div>
      {!reportRecordingAvailable() && <p className="text-xs text-[var(--color-text-muted)]">{t.recordingUnavailable}</p>}
      {reportRecordingAvailable() && <p className="text-xs text-[var(--color-text-muted)]">{t.recordHint}</p>}
      {recordingErrorMessage && (
        <p role="alert" className="text-sm text-[var(--color-danger)]">{recordingErrorMessage}</p>
      )}
      <p className="text-xs leading-5 text-[var(--color-text-muted)]">
        {t.imageHelp}
        <br />
        {t.privacy}
      </p>
      {error && (
        <p role="alert" className="text-sm">
          {t.captureFailed}
        </p>
      )}
      {state.images.map((image, index) => image.kind === 'video' ? (
        <ReportVideo key={image.key} image={image} index={index} controller={controller}
          editable={editable && !state.preparing} approved={Boolean(image.previewUrl && approved.has(image.previewUrl))}
          onApproval={onApproval} />
      ) : (
        <ImageEditor
          key={image.key}
          image={image}
          index={index}
          controller={controller}
          editable={editable && !state.preparing}
          approved={Boolean(image.previewUrl && approved.has(image.previewUrl))}
          onApproval={onApproval}
        />
      ))}
    </section>
  )
}
